import classNames from 'classnames';
import {
    forwardRef,
    type ButtonHTMLAttributes,
    type KeyboardEvent,
    type MouseEvent,
    type ReactNode,
} from 'react';
import styles from './FJIconBox.module.less';
import FJToolTip from '../FJToolTip/FJToolTip';
import FJLoading from '../FJLoading/FJLoading';

interface FJIconBoxProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'children'> {
    /** 左侧图标（通常为 SVG 组件） */
    icon: ReactNode;
    /** 有值时在图标右侧展示 */
    text?: ReactNode;
    tooltipTitle?: string;
    isLoading?: boolean;
    children?: ReactNode;
}

function shouldShowText(text: ReactNode | undefined): boolean {
    if (text == null) {
        return false;
    }
    if (typeof text === 'string' || typeof text === 'number') {
        return String(text).length > 0;
    }
    return true;
}

const FJIconBox = forwardRef<HTMLButtonElement, FJIconBoxProps>(function FJIconBox(
    { className, onClick, icon, text, tooltipTitle, isLoading, children, onKeyDown, ...rest },
    ref,
) {
    const showText = shouldShowText(text);

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
        if (isLoading) {
            return;
        }
        onClick?.(e);
        if (e.detail > 0) {
            e.currentTarget.blur();
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'Enter' || e.key === ' ' || e.code === 'Space') {
            e.preventDefault();
        }
        onKeyDown?.(e);
    };

    return (
        <FJToolTip title={tooltipTitle ?? ''}>
            <button
                ref={ref}
                type='button'
                className={classNames(styles.root, showText && styles.root_with_text, className)}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                {...rest}
            >
                <span className={styles.icon_slot}>
                    {isLoading ? <FJLoading style={{ width: 18, height: 18 }} strokeColor='#080a10' /> : icon}
                </span>
                {showText ? <span className={styles.text}>{text}</span> : null}
                {children}
            </button>
        </FJToolTip>
    );
});

export default FJIconBox;
